import {computed, inject, Injectable, signal} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {WebPushRepositoryService, WebPushService, WebPushSubscription} from '@xmedical/core';
import {UserStore} from './user.store';

/**
 * NotificationStore
 *
 * Manages web push notification state for the xmedical-app.
 * Components inject this store — never repositories or WebPushService directly.
 *
 * State:
 * - isSupported, permissionState, isSubscribed — delegated from WebPushService
 * - subscriptions — list of all registered devices for the current user
 * - loading — async operation in progress
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationStore {
  private pushService = inject(WebPushService);
  private pushRepo = inject(WebPushRepositoryService);
  private userStore = inject(UserStore);

  // Delegated from WebPushService
  readonly isSupported = this.pushService.isSupported;
  readonly permissionState = this.pushService.permissionState;
  readonly isSubscribed = this.pushService.isSubscribed;
  readonly canSubscribe = this.pushService.canSubscribe;
  readonly isDenied = this.pushService.isDenied;

  // Additional store-level state
  readonly subscriptions = signal<WebPushSubscription[]>([]);
  readonly loading = signal<boolean>(false);
  readonly subscribing = signal<boolean>(false);

  // Whether the user has opted in server-side
  readonly webPushEnabled = computed(() => this.userStore.settings()?.webPushEnabled ?? false);

  /**
   * Request browser notification permission and subscribe to push.
   * Called when user clicks "Allow Browser Notifications" on the settings page.
   */
  enablePush(): Observable<boolean> {
    this.subscribing.set(true);
    return this.pushService.enablePush().pipe(
      tap(() => this.subscribing.set(false)),
      catchError(() => {
        this.subscribing.set(false);
        return of(false);
      })
    );
  }

  /**
   * Unsubscribe from push notifications.
   */
  disablePush(): Observable<boolean> {
    this.subscribing.set(true);
    return this.pushService.disablePush().pipe(
      tap(() => this.subscribing.set(false)),
      catchError(() => {
        this.subscribing.set(false);
        return of(false);
      })
    );
  }

  /**
   * Load the user's registered push subscriptions from the server.
   */
  loadSubscriptions(): Observable<WebPushSubscription[]> {
    this.loading.set(true);
    return this.pushRepo.getSubscriptions().pipe(
      tap(subs => {
        this.subscriptions.set(subs);
        this.loading.set(false);
      }),
      catchError(() => {
        this.loading.set(false);
        return of([]);
      })
    );
  }

  /**
   * Remove a specific device subscription by endpoint.
   */
  removeSubscription(endpoint: string): Observable<boolean> {
    this.loading.set(true);
    return this.pushRepo.unsubscribe(endpoint).pipe(
      tap(success => {
        if (success) {
          this.subscriptions.update(subs => subs.filter(s => s.endpoint !== endpoint));
        }
        this.loading.set(false);
      }),
      catchError(() => {
        this.loading.set(false);
        return of(false);
      })
    );
  }

  /**
   * Boot subscription check. Called on app start when the user has opted in.
   * Verifies the browser push subscription is still registered server-side,
   * re-subscribes if missing, and updates the isSubscribed signal.
   */
  bootCheck(): Observable<void> {
    return this.pushService.checkAndRefreshSubscription().pipe(
      catchError(() => of(void 0))
    );
  }

  /**
   * Subscribe this user to the Pusher presence channel for online-presence tracking.
   * The backend suppresses push notifications when the channel is occupied.
   */
  subscribeToPresenceChannel(): void {
    const userId = this.userStore.userId();
    if (userId && userId !== 'public') {
      this.pushService.subscribeToPresenceChannel(userId);
    }
  }
}
